import { DISTRIBUTION_STEP_COUNT } from "../../constants/distributionHelpContent";
import type { DistributionStepNumber } from "../../constants/distributionHelpContent";
import type { DistributionMode } from "../../services/distributions/distributionModeMemory";

export interface DistributionStoryParams {
  workspaceId: string;
  storyId: string;
}

/**
 * How a new distribution is set up (pd7 §11): Quick mode is the default way in; the full wizard is
 * the advanced one (`?mode=advanced`).
 */
export type DistributionCreateMode = "quick" | "advanced";

/** Every distribution route is a child of `/workspace/:workspaceId/story/:storyId`. */
const base = ({ workspaceId, storyId }: DistributionStoryParams) =>
  `/workspace/${workspaceId}/story/${storyId}`;

export const distributionPaths = {
  story: (params: DistributionStoryParams) => `${base(params)}/present`,
  list: (params: DistributionStoryParams) => `${base(params)}/distributions`,
  create: (params: DistributionStoryParams, mode: DistributionCreateMode = "quick") =>
    `${base(params)}/distributions/new${mode === "advanced" ? "?mode=advanced" : ""}`,
  /**
   * Without a step or mode, Edit opens in the last-used mode (D7a when none is stored). A step means
   * Advanced; `mode: "simple"` opens Simple on the same draft.
   */
  edit: (params: DistributionStoryParams, distributionId: string, step?: DistributionStepNumber, mode?: DistributionMode) =>
    `${base(params)}/distributions/${distributionId}/edit${
      step ? `?step=${step}` : mode === "simple" ? "?mode=simple" : ""
    }`,
  /** Round 6: a distribution's runs; `ask` opens it with the Send now confirm showing. */
  runs: (params: DistributionStoryParams, distributionId: string, ask = false) =>
    `${base(params)}/distributions/${distributionId}/runs${ask ? "?send=1" : ""}`,
  run: (params: DistributionStoryParams, distributionId: string, runId: string) =>
    `${base(params)}/distributions/${distributionId}/runs/${runId}`,
  delivery: (params: DistributionStoryParams, distributionId: string, runId: string, deliveryId: string) =>
    `${base(params)}/distributions/${distributionId}/runs/${runId}/deliveries/${deliveryId}`,
};

/** True when the URL names a step at all; without one the wizard resumes the draft. */
export const hasDistributionStep = (raw: string | null): boolean => raw !== null && raw !== "";

/** `?step=3` → 3; anything else → 1. */
export const parseDistributionStep = (raw: string | null): DistributionStepNumber => {
  const step = Number(raw);
  return step >= 1 && step <= DISTRIBUTION_STEP_COUNT && Number.isInteger(step) ? (step as DistributionStepNumber) : 1;
};

/** Round 6: `?send=1` on the runs list opens the Send now confirm. */
export const wantsSendConfirm = (raw: string | null): boolean => raw === "1";

/**
 * `/distributions/new` opens Quick mode; `?mode=advanced` opens the wizard. A `?step=` also means the
 * wizard: it moves between its steps by rewriting the query, which drops `mode`.
 */
export const parseDistributionCreateMode = (mode: string | null, step: string | null): DistributionCreateMode =>
  mode === "advanced" || hasDistributionStep(step) ? "advanced" : "quick";

/**
 * The mode an Edit URL names, or null when it names none (then the stored mode, or D7a, decides).
 * A `?step=` always means Advanced, so every existing wizard URL keeps opening the wizard.
 */
export const parseDistributionEditMode = (mode: string | null, step: string | null): DistributionMode | null =>
  mode === "advanced" || hasDistributionStep(step) ? "advanced" : mode === "simple" ? "simple" : null;
